import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { format, addDays, isSunday, addYears } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const methodsData = [
  { method: "Radiographic Testing" },
  { method: "Ultrasonic Testing" },
  { method: "Magnetic Particle Testing" },
  { method: "Liquid Penetrant Testing" },
  { method: "Visual Testing" },
];

const methodShortMap = {
  "Radiographic Testing": "RT",
  "Ultrasonic Testing": "UT",
  "Magnetic Particle Testing": "MPT",
  "Liquid Penetrant Testing": "LPT",
  "Visual Testing": "VT",
};

function getNextValidDate(startDate, daysToAdd = 1) {
  let date = addDays(startDate, daysToAdd);
  while (isSunday(date)) {
    date = addDays(date, 1);
  }
  return date;
}

function generateUniqueId(length = 8) {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randIndex);
  }
  return result;
}

export default function CertificateGenerator() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [studentName, setStudentName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [certificates, setCertificates] = useState([]);

  async function checkUniqueId(uid) {
    try {
      const response = await fetch(`https://gravitechinstitute.com/certificate/check_certificate.php?uid=${uid}`);
      const text = await response.text(); // First get raw text
      const json = JSON.parse(text);      // Then parse manually
      return json.exists;
    } catch (error) {
      console.error("Error checking unique ID:", error);
      return false;
    }
  }

  const generateCertificates = async () => {
    if (!studentName || !startDate) return;

    if (certificates.length > 0) {
      const confirmGenerate = window.confirm("Are you ready to generate a new data?");
      if (!confirmGenerate) return;
    }

    const start = new Date(startDate);
    let examDate = getNextValidDate(start, 0);
    let certs = [];

    let sharedUniqueId = "";
    let isDuplicate = true;

    // 🔄 Loop until a unique ID is found
    while (isDuplicate) {
      const tempId = generateUniqueId();
      isDuplicate = await checkUniqueId(tempId);
      if (!isDuplicate) sharedUniqueId = tempId;
    }

    methodsData.forEach((item) => {
      const general = +(Math.random() * 10 + 80).toFixed(1);
      const specific = +(Math.random() * 10 + 80).toFixed(1);
      const practical = +(Math.random() * 10 + 80).toFixed(1);
      const overall = ((general + specific + practical) / 3).toFixed(1);

      const issuedDate = getNextValidDate(examDate, 2);
      const validUpto = addDays(addYears(issuedDate, 5), -1);
      const year = new Date(examDate).getFullYear();
      const shortCode = methodShortMap[item.method] || "UNK";

      certs.push({
        name: studentName,
        method: item.method,
        examDate: format(examDate, "dd/MM/yyyy"),
        issuedDate: format(issuedDate, "dd/MM/yyyy"),
        validUpto: format(validUpto, "dd/MM/yyyy"),
        marks: { general, specific, practical, overall },
        certificateNumber: `GIS/${year}/NDT/${shortCode}/${sharedUniqueId}`,
      });

      examDate = getNextValidDate(examDate, 1);
    });

    setCertificates(certs);

    // ✅ Send to backend
    fetch("https://gravitechinstitute.com/certificate/save_certificates.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ students_certificates: certs }),
    })
      .then((res) => res.text())
      .then((data) => {
        console.log("Server response:", data);
      })
      .catch((err) => {
        console.error("Error sending data:", err);
      });
  };

  const downloadSVG = (qrData, filename) => {
    const temp = document.createElement("div");
    document.body.appendChild(temp);
    const root = createRoot(temp);
    root.render(<QRCodeSVG value={qrData} size={150} level="H" includeMargin={true} />);

    setTimeout(() => {
      const svg = temp.querySelector("svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      root.unmount();
      document.body.removeChild(temp);
    }, 100);
  };

  const downloadAllAsZip = async () => {
    if (!certificates.length) return;

    const zip = new JSZip();
    const qrFolder = zip.folder("qr_codes");

    for (let cert of certificates) {
      const qrData = `https://certificate.gravitechinstitute.com/?certificate=${cert.certificateNumber.split("/").pop()}`;

      const temp = document.createElement("div");
      document.body.appendChild(temp);
      const root = createRoot(temp);
      root.render(<QRCodeSVG value={qrData} size={150} level="H" includeMargin={true} />);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const svg = temp.querySelector("svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      qrFolder.file(`QR-${cert.name}-${cert.method}.svg`, svgData);
      root.unmount();
      document.body.removeChild(temp);
    }

    const txtContent = certificates
      .map(
        (cert) => `${cert.name}
Certificate No: ${cert.certificateNumber}
Method: ${cert.method}
Level Two
Details of examination: General - ${cert.marks.general}%, Specific - ${cert.marks.specific}%, Practical - ${cert.marks.practical}%, Overall Result - ${cert.marks.overall}%
Date of Examination: ${cert.examDate}
Issued Date: ${cert.issuedDate}
Valid Upto: ${cert.validUpto}`
      )
      .join("\n--------------------------\n\n");

    const jsonContent = JSON.stringify(certificates, null, 2);

    zip.file(`${studentName.replace(/\s+/g, "_")}_Certificates.txt`, txtContent);
    zip.file(`${studentName.replace(/\s+/g, "_")}_Certificates.json`, jsonContent);

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `${studentName.replace(/\s+/g, "_")}_Certificates_Data.zip`);
  };

  // 🔐 Render the password screen first
  if (!isAuthenticated) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>🔒 Enter Password to Access Generator</h2>
        <input
          type="password"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            padding: "10px",
            marginRight: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />
        <button
          onClick={() => {
            if (password === "GIS@2013") setIsAuthenticated(true);
            else alert("Wrong password!");
          }}
          style={{
            padding: "10px 20px",
            borderRadius: "6px",
            border: "none",
            backgroundColor: "#2e317d",
            color: "#fff",
          }}
        >
          Submit
        </button>
      </div>
    );
  }

  // ✅ Authenticated view below
  return (
    <div style={{ padding: "40px", fontFamily: "'Segoe UI', sans-serif", maxWidth: "900px", margin: "0 auto" }}>
      <h2 style={{ fontSize: "28px", marginBottom: "20px", color: "#2e317dff" }}>
        Gravitech Certificate Generator
      </h2>

      {/* INPUTS */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Enter Student Name"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          style={{
            flex: "1",
            padding: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            minWidth: "250px",
          }}
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            minWidth: "150px",
          }}
        />
        <button
          onClick={generateCertificates}
          style={{
            backgroundColor: "#2e7d32",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Generate
        </button>
      </div>

      {/* ZIP DOWNLOAD */}
      {certificates.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <button
            onClick={downloadAllAsZip}
            style={{
              padding: "12px 24px",
              backgroundColor: "#2e317d",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            📦 Download All as ZIP (QR + TXT + JSON)
          </button>
        </div>
      )}

      {/* CERTIFICATES */}
      <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
        {certificates.map((cert, index) => {
          const qrData = `https://certificate.gravitechinstitute.com/?certificate=${cert.certificateNumber.split("/").pop()}`;

          return (
            <div
              key={index}
              style={{
                border: "1px solid #ccc",
                borderRadius: "10px",
                padding: "20px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <h4>{cert.name}</h4>
              <p><strong>Certificate No:</strong> {cert.certificateNumber}</p>
              <p><strong>Method:</strong> {cert.method}</p>
              <p>
                <strong>Marks:</strong> General - {cert.marks.general}%, Specific - {cert.marks.specific}%, Practical - {cert.marks.practical}%, <strong>Overall:</strong> {cert.marks.overall}%
              </p>
              <p>
                <strong>Exam:</strong> {cert.examDate}<br />
                <strong>Issued:</strong> {cert.issuedDate}<br />
                <strong>Valid Upto:</strong> {cert.validUpto}
              </p>
              <button
                onClick={() => downloadSVG(qrData, `QR-${cert.name}-${cert.method}.svg`)}
                style={{
                  marginTop: "10px",
                  padding: "8px 14px",
                  backgroundColor: "#00796b",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                ⬇️ Download QR (SVG)
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
